/**
 * `// @param` annotations → parameter definitions.
 *
 * Parsed on the host, not inside the sandbox: the settings panel and the
 * runtime read the same definitions, and nothing the iframe says is trusted.
 *
 * Canonical form (type first, then min max default, then an optional label):
 *   // @param float speed 0 5 1 Animation speed
 *   // @param int count 1 1000 100
 *   // @param bool enabled true
 *   // @param color tint #ff00aa
 *   // @param select mode add,multiply,screen multiply
 *
 * The older name-first form (default min max) is still read:
 *   // @param speed float 1 0 5 Animation speed
 */

import { GLSL_RESERVED_UNIFORMS } from './media/contract';
import type { ExtractedParam, ParamType } from './types';

const ANNOTATION = /^[ \t]*\/\/[ \t]*@param[ \t]+(.+?)[ \t]*$/gm;
const UNIFORM = /^[ \t]*uniform[ \t]+(?:(?:lowp|mediump|highp)[ \t]+)?(float|int|bool|vec3|vec4)[ \t]+([A-Za-z_]\w*)(?:[ \t]*=[ \t]*[^;]+)?[ \t]*;/gm;
const PARAM_TYPES: ReadonlySet<string> = new Set<ParamType>(['float', 'int', 'bool', 'color', 'select']);
const IDENTIFIER = /^[A-Za-z_]\w*$/;
const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const RESERVED_UNIFORMS = new Set<string>(GLSL_RESERVED_UNIFORMS);

export function parseParams(code: string): ExtractedParam[] {
  const params: ExtractedParam[] = [];
  const seen = new Set<string>();
  for (const match of code.matchAll(ANNOTATION)) {
    const tokens = match[1].split(/[ \t]+/);
    const param = PARAM_TYPES.has(tokens[0]) ? typeFirst(tokens) : nameFirst(tokens);
    if (param && !seen.has(param.name)) {
      seen.add(param.name);
      params.push(param);
    }
  }
  // A declared uniform is a parameter even without a special comment. The runtime
  // already binds these names; annotations still win when both describe one name.
  for (const match of code.matchAll(UNIFORM)) {
    const param = uniformParam(match[1], match[2]);
    if (param && !seen.has(param.name) && !RESERVED_UNIFORMS.has(param.name)) {
      seen.add(param.name);
      params.push(param);
    }
  }
  return params;
}

function uniformParam(type: string, name: string): ExtractedParam | null {
  if (!IDENTIFIER.test(name)) return null;
  const label = uniformLabel(name);
  switch (type) {
    case 'float':
      return { name, type: 'float', min: 0, max: 1, default: 0.5, label };
    case 'int':
      return { name, type: 'int', min: 0, max: 10, default: 1, label };
    case 'bool':
      return { name, type: 'bool', default: false, label };
    case 'vec3':
    case 'vec4':
      return /color|tint|colour/i.test(name) ? { name, type: 'color', default: '#ffffff', label } : null;
    default:
      return null;
  }
}

function uniformLabel(name: string): string {
  const bare = name.replace(/^u_/, '').replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return bare.charAt(0).toUpperCase() + bare.slice(1);
}

/**
 * Parsed definitions win; a label from `previous` (e.g. a library sketch's
 * friendlier "Cell Size") is kept when the annotation doesn't carry one.
 */
export function mergeParamLabels(parsed: ExtractedParam[], previous: readonly ExtractedParam[] | undefined): ExtractedParam[] {
  if (!previous?.length) return parsed;
  return parsed.map((param) => {
    if (param.label) return param;
    const label = previous.find((p) => p.name === param.name && p.type === param.type)?.label;
    return label ? { ...param, label } : param;
  });
}

/** Current value for a param, falling back to its default when missing or the wrong kind. */
export function paramValue(param: ExtractedParam, values: Record<string, unknown>): number | string | boolean {
  const value = values[param.name];
  switch (param.type) {
    case 'float':
    case 'int':
      return typeof value === 'number' && Number.isFinite(value) ? value : param.default;
    case 'bool':
      return typeof value === 'boolean' ? value : param.default;
    case 'color':
      return typeof value === 'string' && HEX_COLOR.test(value) ? normalizeColor(value) : param.default;
    case 'select':
      return typeof value === 'string' && param.options?.includes(value) ? value : param.default;
  }
}

function typeFirst([type, name, ...rest]: string[]): ExtractedParam | null {
  if (!name || !IDENTIFIER.test(name)) return null;
  switch (type as ParamType) {
    case 'float':
    case 'int': {
      const numbers = leadingNumbers(rest, 3);
      const [min, max, fallback] = numbers.length === 3 ? numbers : numbers.length === 2 ? [numbers[0], numbers[1], numbers[0]] : [undefined, undefined, numbers[0] ?? 0];
      return numeric(type as 'float' | 'int', name, fallback, min, max, rest.slice(numbers.length));
    }
    case 'bool':
      return boolean(name, rest);
    case 'color':
      return color(name, rest);
    case 'select': {
      const options = splitOptions(rest[0]);
      if (!options.length) return null;
      const hasDefault = rest[1] !== undefined && options.includes(rest[1]);
      return withLabel({ name, type: 'select', options, default: hasDefault ? rest[1] : options[0] }, rest.slice(hasDefault ? 2 : 1));
    }
    default:
      return null;
  }
}

function nameFirst([name, type, ...rest]: string[]): ExtractedParam | null {
  if (!name || !IDENTIFIER.test(name) || !type || !PARAM_TYPES.has(type)) return null;
  switch (type as ParamType) {
    case 'float':
    case 'int': {
      const numbers = leadingNumbers(rest, 3);
      return numeric(type as 'float' | 'int', name, numbers[0] ?? 0, numbers[1], numbers[2], rest.slice(numbers.length));
    }
    case 'bool':
      return boolean(name, rest);
    case 'color':
      return color(name, rest);
    case 'select': {
      const options = splitOptions(rest[0]);
      return options.length ? withLabel({ name, type: 'select', options, default: options[0] }, rest.slice(1)) : null;
    }
  }
}

function numeric(type: 'float' | 'int', name: string, fallback: number, min: number | undefined, max: number | undefined, labelTokens: string[]): ExtractedParam {
  const round = (n: number | undefined) => (n === undefined || type === 'float' ? n : Math.round(n));
  const [lo, hi] = min !== undefined && max !== undefined && min > max ? [max, min] : [min, max];
  return withLabel({ name, type, default: round(fallback) ?? 0, min: round(lo), max: round(hi) }, labelTokens);
}

function boolean(name: string, rest: string[]): ExtractedParam {
  const hasDefault = rest[0] === 'true' || rest[0] === 'false';
  return withLabel({ name, type: 'bool', default: rest[0] === 'true' }, rest.slice(hasDefault ? 1 : 0));
}

function color(name: string, rest: string[]): ExtractedParam {
  const hasDefault = rest[0] !== undefined && HEX_COLOR.test(rest[0]);
  return withLabel({ name, type: 'color', default: hasDefault ? normalizeColor(rest[0]) : '#ffffff' }, rest.slice(hasDefault ? 1 : 0));
}

function withLabel(param: ExtractedParam, labelTokens: string[]): ExtractedParam {
  const label = labelTokens.join(' ').trim();
  const clean = Object.fromEntries(Object.entries(param).filter(([, v]) => v !== undefined)) as ExtractedParam;
  return label ? { ...clean, label } : clean;
}

function leadingNumbers(tokens: string[], limit: number): number[] {
  const numbers: number[] = [];
  for (const token of tokens.slice(0, limit)) {
    if (!/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(token)) break;
    numbers.push(Number(token));
  }
  return numbers;
}

function splitOptions(token: string | undefined): string[] {
  return token ? token.split(',').map((o) => o.trim()).filter(Boolean) : [];
}

function normalizeColor(hex: string): string {
  const h = hex.slice(1).toLowerCase();
  return h.length === 3 ? `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}` : `#${h}`;
}
